/**
 * Reads the #swatch-catalog data island into plain records.
 *
 * The island is written by Helpers/SwatchCatalogJson.cs and is index-compressed: repeated text
 * (category, collection, product line name, style keys) lives in shared tables, and anything
 * constant across a product line lives on the group rather than on every swatch. This expands
 * that back into flat records so callers never deal with the encoding.
 *
 * Returns null when there is no island. That is not an error condition - the My Account
 * favorites page reuses swatch-detail.js and swatch-cart-favorites.js with its own DOM and no
 * catalog, so every consumer has to keep working without one.
 */

const CATALOG_ELEMENT_ID = 'swatch-catalog';
const FAVORITES_ELEMENT_ID = 'swatch-favorites';

const readCatalog = (doc) => {
  const island = (doc || document).getElementById(CATALOG_ELEMENT_ID);

  if (!island) {
    return null;
  }

  let raw;

  try {
    raw = JSON.parse(island.textContent);
  } catch (e) {
    // A malformed island must not take the page down with it: every consumer falls back to the
    // DOM, which is still rendered.
    return null;
  }

  if (!raw || !raw.swatches || !raw.groups) {
    return null;
  }

  const strings = raw.strings || [];
  const styleKeyTable = raw.styleKeys || [];
  const text = index => strings[index] || '';

  const records = raw.swatches.map((s) => {
    const group = raw.groups[s.g] || {};

    return {
      id: String(s.id),

      // Which group this swatch belongs to, as an index into raw.groups. The server renders
      // .swatch-filtered-group containers in the same order, so this doubles as the position of
      // the container a rendered card belongs in - exact in a way that matching on
      // tab + productLineId would not be if a line ever appeared under one tab twice.
      groupIndex: s.g,

      tab: group.tab || '',
      productLineId: String(group.lineId),
      productLineName: text(group.lineName),
      visualizeUrl: text(group.visualizeUrl),
      category: text(s.cat),
      collection: text(s.coll),
      colorName: s.name || '',
      colorNumber: s.num || '',
      productLineCode: s.code || '',
      imageUrl: raw.imageBase + s.img,
      isNew: s.new === 1,

      // Ids are compared as strings everywhere in the swatch JS, because they arrive as strings
      // from data attributes and url params. Normalized here so callers never have to think
      // about which side of the comparison came from where.
      colorRangeIds: (s.cr || []).map(String),
      solutionIds: (s.sol || []).map(String),
      styleKeys: (s.sk || []).map(i => styleKeyTable[i] || ''),
    };
  });

  const byId = {};
  records.forEach((record) => {
    byId[record.id] = record;
  });

  return {
    records,
    byId,
    get: id => byId[String(id)] || null,
  };
};

/**
 * Reads the per-visitor favorites island into a swatch id -> asset id map.
 *
 * Returns an empty map rather than null when the island is missing: "no favorites" and "not
 * signed in" render the same card, so callers have no reason to distinguish them.
 */
const readFavorites = (doc) => {
  const island = (doc || document).getElementById(FAVORITES_ELEMENT_ID);

  if (!island) {
    return {};
  }

  try {
    const raw = JSON.parse(island.textContent);
    return (raw && raw.assets) || {};
  } catch (e) {
    return {};
  }
};

export default readCatalog;
export {
  readCatalog, readFavorites, CATALOG_ELEMENT_ID, FAVORITES_ELEMENT_ID,
};
